import {
  CUSTOM_FIELD_TYPES,
  CUSTOM_FIELD_TYPE_LABELS,
  fieldKeyFromLabel,
  isOptionType,
  type CustomFieldType,
} from '@church/domain'
import {
  useCustomFieldMutations,
  useCustomFields,
  useCustomFieldSectionMutations,
  useCustomFieldSections,
  useAccess,
  type CustomFieldDefinition,
  type CustomFieldSection,
} from '@church/data'
import {
  faAsterisk,
  faBoxArchive,
  faFloppyDisk,
  faInputText,
  faLayerGroup,
  faPlus,
  faTrashCan,
  faXmark,
} from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  ListCard,
  ListRow,
  ListSection,
  NewButton,
  usePanel,
} from '../components/platform/SettingsList.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  PanelPrimary,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  Select,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'

/**
 * Custom field definitions. A field's *type* is deliberately not editable
 * here: values are stored in the column matching the type they were written
 * as, and re-interpreting them later is how "1998" quietly becomes a number
 * in a field that meant a year of baptism. Retire the field and make a new one.
 */
export const Route = createFileRoute('/_authed/_shell/settings/custom-fields')({
  component: CustomFieldsTab,
})

interface OptionDraft {
  id?: string
  label: string
}

function CustomFieldsTab() {
  const { data: fields, isPending } = useCustomFields()
  const { data: sections } = useCustomFieldSections()
  const { remove } = useCustomFieldMutations()
  const access = useAccess()
  const panel = usePanel<string>()
  const field = panel.shown === 'new' ? undefined : fields?.find((f) => f.id === panel.shown)
  const sectionName = (id: string | null) => sections?.find((entry) => entry.id === id)?.name

  return (
    <div className="max-w-2xl">
      <ListSection
        title="Custom fields"
        icon={faInputText}
        action={
          access.has('people', 'administrator', { orgWide: true }) && (
            <NewButton label="New field" onClick={() => panel.show('new')} />
          )
        }
      >
        {isPending ? (
          <Spinner />
        ) : !fields || fields.length === 0 ? (
          <Card>
            <EmptyState
              title="No custom fields"
              hint="A baptism date, a T-shirt size, which service someone usually attends."
            />
          </Card>
        ) : (
          <ListCard>
            {fields.map((f) => (
              <ListRow
                key={f.id}
                title={f.label}
                meta={f.options.length > 0 ? f.options.map((o) => o.label).join(' · ') : undefined}
                aside={
                  <>
                    {f.required && (
                      <Chip tone="warning">
                        <Icon icon={faAsterisk} className="size-2.5" />
                        Required
                      </Chip>
                    )}
                    {sectionName(f.sectionId) && (
                      <Chip tone="violet">{sectionName(f.sectionId)}</Chip>
                    )}
                    <Chip>{CUSTOM_FIELD_TYPE_LABELS[f.type]}</Chip>
                  </>
                }
                onOpen={
                  access.has('people', 'administrator', { orgWide: true })
                    ? () => panel.show(f.id)
                    : undefined
                }
              />
            ))}
          </ListCard>
        )}
      </ListSection>

      <SidePanel
        open={panel.open && (panel.shown === 'new' || field !== undefined)}
        onOpenChange={panel.onOpenChange}
        title={field ? field.label : 'New field'}
        description={field ? `${CUSTOM_FIELD_TYPE_LABELS[field.type]} · ${field.key}` : undefined}
        footer={
          field ? (
            <ConfirmButton
              label="Retire field"
              icon={faBoxArchive}
              variant="quietDanger"
              className="-ml-4"
              disabled={remove.isPending}
              title={`Retire “${field.label}”?`}
              description="Every person’s answer to this field goes with it."
              confirmLabel="Retire field"
              onConfirm={() => remove.mutate(field.id, { onSuccess: panel.close })}
            />
          ) : undefined
        }
      >
        <FieldForm
          key={field?.id ?? 'new'}
          field={field}
          sections={sections ?? []}
          onDone={panel.close}
        />
        <ErrorNote error={remove.error} />
      </SidePanel>

      <SectionsList
        sections={sections ?? []}
        editable={access.has('people', 'administrator', { orgWide: true })}
      />
    </div>
  )
}

/**
 * Sections group fields on a profile, each its own card — Planning Center's
 * tabs. Removing one moves its fields back under "Custom fields".
 */
function SectionsList({
  sections,
  editable,
}: {
  sections: CustomFieldSection[]
  editable: boolean
}) {
  const panel = usePanel<string>()
  const section = panel.shown === 'new' ? undefined : sections.find((s) => s.id === panel.shown)
  const { remove } = useCustomFieldSectionMutations()

  return (
    <div className="mt-8">
      <ListSection
        title="Profile sections"
        icon={faLayerGroup}
        action={editable && <NewButton label="New section" onClick={() => panel.show('new')} />}
      >
        {sections.length === 0 ? (
          <Card>
            <EmptyState
              title="No sections"
              hint="Group fields on a profile: Medical, Membership class, Baptism."
            />
          </Card>
        ) : (
          <ListCard>
            {sections.map((entry) => (
              <ListRow
                key={entry.id}
                title={entry.name}
                aside={<Chip>Order {entry.sortOrder}</Chip>}
                onOpen={editable ? () => panel.show(entry.id) : undefined}
              />
            ))}
          </ListCard>
        )}
      </ListSection>
      <SidePanel
        open={panel.open && (panel.shown === 'new' || section !== undefined)}
        onOpenChange={panel.onOpenChange}
        title={section ? section.name : 'New section'}
        footer={
          section ? (
            <ConfirmButton
              label="Remove section"
              icon={faTrashCan}
              variant="quietDanger"
              className="-ml-4"
              disabled={remove.isPending}
              title={`Remove “${section.name}”?`}
              description="Its fields stay, under Custom fields."
              confirmLabel="Remove section"
              onConfirm={() => remove.mutate(section.id, { onSuccess: panel.close })}
            />
          ) : undefined
        }
      >
        <SectionForm key={section?.id ?? 'new'} section={section} onDone={panel.close} />
        <ErrorNote error={remove.error} />
      </SidePanel>
    </div>
  )
}

function SectionForm({ section, onDone }: { section?: CustomFieldSection; onDone: () => void }) {
  const { create, update } = useCustomFieldSectionMutations()
  const mutation = section ? update : create
  const [name, setName] = useState(section?.name ?? '')
  const [sortOrder, setSortOrder] = useState(String(section?.sortOrder ?? 0))
  const order = Number(sortOrder)
  const valid = name.trim() !== '' && Number.isInteger(order) && order >= 0 && order <= 999

  const submit = () => {
    const body = { name: name.trim(), sortOrder: order }
    if (section) update.mutate({ id: section.id, ...body }, { onSuccess: onDone })
    else create.mutate(body, { onSuccess: onDone })
  }

  return (
    <div className="space-y-5">
      <Field label="Name">
        <Input placeholder="Medical" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Order" description="Lower numbers come first on a profile.">
        <Input
          type="number"
          min={0}
          max={999}
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </Field>
      <ErrorNote error={mutation.error} />
      <PanelPrimary>
        <Button
          icon={section ? faFloppyDisk : faPlus}
          className="py-1.5"
          disabled={!valid}
          pending={mutation.isPending}
          onClick={submit}
        >
          {section ? 'Save' : 'Add'}
        </Button>
      </PanelPrimary>
    </div>
  )
}

function FieldForm({
  field,
  sections,
  onDone,
}: {
  field?: CustomFieldDefinition
  sections: CustomFieldSection[]
  onDone: () => void
}) {
  const { create, update } = useCustomFieldMutations()
  const mutation = field ? update : create

  const [label, setLabel] = useState(field?.label ?? '')
  const [type, setType] = useState<CustomFieldType>(field?.type ?? 'text')
  const [required, setRequired] = useState(field?.required ?? false)
  const [sectionId, setSectionId] = useState<string>(field?.sectionId ?? '')
  const [options, setOptions] = useState<OptionDraft[]>(
    field?.options.map((o) => ({ id: o.id, label: o.label })) ?? [],
  )

  const needsOptions = isOptionType(type)
  const filled = options.filter((o) => o.label.trim() !== '')
  const valid = label.trim() !== '' && (!needsOptions || filled.length > 0)

  const submit = () => {
    const payload = {
      label: label.trim(),
      // A file is uploaded, never typed: it cannot be required.
      required: type === 'file' ? false : required,
      sectionId: sectionId || null,
      ...(needsOptions ? { options: filled } : {}),
    }
    if (field) {
      update.mutate({ fieldId: field.id, ...payload }, { onSuccess: onDone })
    } else {
      create.mutate(
        { ...payload, type },
        {
          onSuccess: () => {
            setLabel('')
            setOptions([])
            setRequired(false)
            setSectionId('')
            onDone()
          },
        },
      )
    }
  }

  return (
    <div className="space-y-5">
      <div className="space-y-5">
        <Field label="Label">
          <Input
            placeholder="Baptism date"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </Field>
        <Field
          label="Type"
          description={field ? 'The type cannot change once values exist.' : undefined}
        >
          <Select
            value={type}
            disabled={!!field}
            onChange={(e) => setType(e.target.value as CustomFieldType)}
          >
            {CUSTOM_FIELD_TYPES.map((t) => (
              <option key={t} value={t}>
                {CUSTOM_FIELD_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Section" description="Where it shows on a profile.">
          <Select value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
            <option value="">Custom fields</option>
            {sections.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {needsOptions && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
            Choices
          </h3>
          {options.map((option, index) => (
            <div key={option.id ?? `new-${index}`} className="flex gap-2">
              <Input
                value={option.label}
                placeholder="9am"
                onChange={(e) =>
                  setOptions((current) =>
                    current.map((o, i) => (i === index ? { ...o, label: e.target.value } : o)),
                  )
                }
              />
              <IconButton
                icon={faXmark}
                label={`Remove ${option.label || 'choice'}`}
                onClick={() => setOptions((current) => current.filter((_, i) => i !== index))}
              />
            </div>
          ))}
          <Button
            variant="secondary"
            icon={faPlus}
            onClick={() => setOptions((current) => [...current, { label: '' }])}
          >
            Add choice
          </Button>
        </div>
      )}

      {type !== 'file' && (
        <label className="flex items-center gap-2 text-sm text-content-muted">
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          Required
        </label>
      )}

      {label.trim() !== '' && !field && (
        <p className="text-xs text-content-subtle">
          Machine name: <code>{fieldKeyFromLabel(label)}</code>
        </p>
      )}

      <ErrorNote error={mutation.error} />

      <PanelPrimary>
        <Button
          icon={field ? faFloppyDisk : faPlus}
          className="py-1.5"
          disabled={!valid}
          pending={mutation.isPending}
          onClick={submit}
        >
          {field ? 'Save' : 'Add'}
        </Button>
      </PanelPrimary>
    </div>
  )
}
